/* RESPONSIBLE TEAM: team-inbox */
import Service, { inject as service } from '@ember/service';
import type Router from '@ember/routing/router-service';
import type Session from './session';
import type InboxState from './inbox-state';
import { InboxEvents } from './inbox-state';
import type InboxSidebarService from './inbox-sidebar-service';
import type CommandKService from './command-k';
import { DisplayContext } from './command-k';
import type PermissionsModal from 'embercom/services/permissions-modal';
import type IntlService from 'embercom/services/intl';
import type ReactTeammateAppIslandService from 'embercom/services/react-teammate-app-island';
import type TracingService from 'embercom/services/tracing';
import type CopilotApi from 'embercom/services/copilot-api';
import Conversation from 'embercom/objects/inbox/conversation';
import { RenderableType } from 'embercom/models/data/inbox/renderable-types';
import type WorkflowConnectorAction from 'embercom/objects/inbox/workflow-connector-action';
import { type BlockList } from '@intercom/interblocks.ts';
import { taskFor } from 'ember-concurrency-ts';

/**
 * Handles bridge event subscriptions between the React conversation stream
 * and Ember services. Extracted from inbox-state.ts to keep bridge plumbing
 * separate from core inbox state logic.
 */
export default class InboxConversationStreamBridgeService extends Service {
  @service declare reactTeammateAppIsland: ReactTeammateAppIslandService;
  @service declare session: Session;
  @service declare inboxState: InboxState;
  @service declare router: Router;
  @service declare inboxSidebarService: InboxSidebarService;
  @service declare commandK: CommandKService;
  @service declare permissionsModal: PermissionsModal;
  @service declare intl: IntlService;
  @service declare tracing: TracingService;
  @service declare copilotApi: CopilotApi;

  private unsubscribers: Array<() => void> = [];

  willDestroy() {
    super.willDestroy();
    for (let unsubscribe of this.unsubscribers) {
      unsubscribe();
    }
    this.unsubscribers = [];
  }

  setupSubscriptions() {
    if (this.unsubscribers.length > 0) {
      return;
    }

    this.unsubscribers.push(
      this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
        'save-reply-as-macro',
        (data: Record<string, unknown>) => {
          if (!this.isStreamEnabled) {
            return;
          }
          this.inboxState.createMacroBlocks = data['blocks'] as BlockList | undefined;
          this.inboxState.createMacroType = 1; // MacroType.Reply
          let hasPermission = this.inboxState.handleCreateMacroAction('react_message_actions');
          if (hasPermission) {
            this.inboxState.showCreateMacroModal = true;
          }
        },
      ),
    );

    this.unsubscribers.push(
      this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
        'reply-to-email-part',
        (data: Record<string, unknown>) => {
          if (!this.isStreamEnabled) {
            return;
          }
          let conversationId = data['conversationId'] as number;
          let partEntityId = data['partEntityId'] as number;
          this.inboxState.replyToEmailPart({ entityId: partEntityId }, conversationId);
        },
      ),
    );

    this.unsubscribers.push(
      this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
        'forward-message',
        (data: Record<string, unknown>) => {
          if (!this.isStreamEnabled) {
            return;
          }
          let conversationId = data['conversationId'] as number;
          let partEntityId = data['partEntityId'] as number;
          let entityType = data['entityType'] as number | undefined;
          let partType = entityType !== undefined && entityType !== 70 ? 'initial-part' : 'comment';
          let partId = `${partType}-${conversationId}-${partEntityId}`;
          this.router.transitionTo('inbox.workspace.inbox.new-conversation', {
            queryParams: { forwardedPartId: partId, ticketTypeId: undefined },
          });
        },
      ),
    );

    this.unsubscribers.push(
      this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
        'sidebar:preview-conversation',
        (data: Record<string, unknown>) => {
          if (!this.isStreamEnabled) {
            return;
          }
          let conversationId = data['conversationId'] as number;
          this.inboxSidebarService.previewConversation({ id: conversationId } as Conversation);
        },
      ),
    );

    this.unsubscribers.push(
      this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
        'sidebar:preview-side-conversation',
        (data: Record<string, unknown>) => {
          if (!this.isStreamEnabled) {
            return;
          }
          let sideConversationId = data['sideConversationId'] as number;
          let parentConversationId = data['parentConversationId'] as number;
          this.inboxSidebarService.previewSideConversation(
            sideConversationId,
            parentConversationId,
            true,
          );
        },
      ),
    );

    this.unsubscribers.push(
      this.reactTeammateAppIsland.subscribeToTeammateAppEvent('trigger-workflow-connector', () => {
        if (!this.isStreamEnabled) {
          return;
        }
        if (!this.session.teammate.permissions.canUseDataConnectors) {
          this.permissionsModal.showModal({
            permissionsLabel: this.intl.t('inbox.common.permissions-label.can-use-data-connectors'),
          });
          return;
        }
        let conversation = this.inboxState.activeConversation;
        if (!conversation || !(conversation instanceof Conversation)) {
          return;
        }
        this.commandK.setActivationContext(DisplayContext.MessageActions);
        this.commandK.registerAndShow({
          actionID: 'trigger-workflow-connector-action-part',
          onSelect: (action: WorkflowConnectorAction) => {
            taskFor(this.inboxState.triggerWorkflowConnectorAction).perform(
              conversation,
              null,
              action,
            );
          },
          activatingComponent: 'conversation-part-message-actions',
        });
      }),
    );

    this.unsubscribers.push(
      this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
        'open-copilot',
        (data: Record<string, unknown>) => {
          if (!this.isStreamEnabled) {
            return;
          }
          let query = data['query'] as string;
          if (query) {
            this.copilotApi.triggerAndSearchCopilotQuery(query);
          }
        },
      ),
    );

    this.unsubscribers.push(
      this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
        'open-fin-answer-debugger',
        (data: Record<string, unknown>) => {
          if (!this.isStreamEnabled) {
            return;
          }
          this.inboxState.finDebuggerPartEntityId = data['partEntityId'] as number;
        },
      ),
    );

    this.unsubscribers.push(
      this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
        'conversation:mark-as-read',
        (data: Record<string, unknown>) => {
          if (!this.isStreamEnabled) {
            return;
          }
          let conversation = this.inboxState.activeConversation;
          if (!conversation || !(conversation instanceof Conversation)) {
            return;
          }
          if (conversation.id !== data['conversationId']) {
            return;
          }
          conversation.isRead = true;
          this.inboxState.trigger(InboxEvents.ConversationRead, conversation);
        },
      ),
    );

    this.unsubscribers.push(
      this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
        'conversation-stream:rendered',
        (data: Record<string, unknown>) => {
          if (!this.isStreamEnabled) {
            return;
          }
          let span = this.tracing.getActiveRootSpanByResource('inbox2:conversation_stream');
          if (span) {
            this.tracing.endChildSpan({
              span,
              attributes: {
                conversation_renderable_part_count: data['renderablePartCount'] as number,
                rendered_by: 'react',
              },
            });
          }
        },
      ),
    );

    this.unsubscribers.push(
      this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
        'conversation-stream:retry',
        (data: Record<string, unknown>) => {
          if (!this.isStreamEnabled) {
            return;
          }
          let conversation = this.inboxState.activeConversation;
          if (!conversation || !(conversation instanceof Conversation)) {
            return;
          }

          let clientAssignedUuid = data['clientAssignedUuid'] as string;
          let failedPart = conversation.pendingParts?.find(
            (p) => p.clientAssignedUuid === clientAssignedUuid,
          );
          if (!failedPart) {
            return;
          }

          let isNote = failedPart.renderableType === RenderableType.AdminNote;
          this.inboxState.retrySendReplyOrNote({
            isNote,
            conversation,
            part: failedPart,
          });
        },
      ),
    );
  }

  private get isStreamEnabled(): boolean {
    return this.session.workspace.isFeatureEnabled('react-inbox-conversation-stream');
  }
}

declare module '@ember/service' {
  interface Registry {
    inboxConversationStreamBridge: InboxConversationStreamBridgeService;
    'inbox-conversation-stream-bridge': InboxConversationStreamBridgeService;
  }
}
